/**
 * 请求体加密（AES-256-CBC + PKCS7）
 *
 * 开启：.env 中设置 REACT_APP_REQUEST_ENCRYPT=true
 * 密钥：REACT_APP_REQUEST_ENCRYPT_KEY（建议 32 字节，不足则右侧补 0）
 * 向量：REACT_APP_REQUEST_ENCRYPT_IV（16 字节，不足则右侧补 0）
 * 表单字段名：REACT_APP_REQUEST_ENCRYPT_FIELD（默认 payload）
 *
 * 服务端需使用相同算法解密 base64 密文，明文为 JSON 字符串，再反序列化为键值对。
 * 注意：前端密钥会打进产物，仅降低明文被抓包可读性，不能替代 HTTPS 与鉴权。
 */
import CryptoJS from 'crypto-js';

function padUtf8(str, len) {
    const s = str != null ? String(str) : '';
    return s.length >= len ? s.slice(0, len) : s.padEnd(len, '0');
}

function isEncryptEnabled() {
    const v = (process.env.REACT_APP_REQUEST_ENCRYPT || '').trim().toLowerCase();
    return v === 'true' || v === '1' || v === 'yes' || v === 'on';
}

function getKeyAndIv() {
    const keyRaw = process.env.REACT_APP_REQUEST_ENCRYPT_KEY || '';
    const ivRaw = process.env.REACT_APP_REQUEST_ENCRYPT_IV || '';
    return {
        key: CryptoJS.enc.Utf8.parse(padUtf8(keyRaw, 32)),
        iv: CryptoJS.enc.Utf8.parse(padUtf8(ivRaw, 16)),
    };
}

/**
 * @param {Record<string, unknown>} plainObject 与 formData 相同的平铺参数
 * @returns {string} Base64 密文（仅密文，不含 CryptoJS 默认 Salt 头）
 */
export function encryptRequestParams(plainObject) {
    const { key, iv } = getKeyAndIv();
    const json = JSON.stringify(plainObject);
    const encrypted = CryptoJS.AES.encrypt(json, key, {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.ciphertext.toString(CryptoJS.enc.Base64);
}

/**
 * 若含 File/Blob，无法安全整体 JSON 加密，退回明文 FormData
 */
function hasBinaryValue(obj) {
    if (!obj || typeof obj !== 'object') return false;
    return Object.values(obj).some(
        (v) =>
            (typeof File !== 'undefined' && v instanceof File) ||
            (typeof Blob !== 'undefined' && v instanceof Blob)
    );
}

/**
 * 加密后的 FormData：仅一个业务字段 + 固定标记字段（供后端识别）
 */
export function buildEncryptedFormData(plainObject) {
    const field =
        process.env.REACT_APP_REQUEST_ENCRYPT_FIELD || 'payload';
    const fd = new FormData();
    fd.append(field, encryptRequestParams(plainObject));
    fd.append('enc', '1');
    return fd;
}

export function shouldEncryptRequestBody() {
    return isEncryptEnabled();
}

export function canEncryptParams(plainObject) {
    return !hasBinaryValue(plainObject);
}
